import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Stack,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Replay as ReplayIcon,
  FolderSpecial as ProjectIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import { apiService } from '../../../services/apiService';
import { formatSize } from '../../../utils/fileUtils';
import { canResubmitObservation } from '../../../utils/observationActions';
import { useObservationSelection } from '../../../hooks/useObservationSelection';
import SelectionBar from '../../../components/files/SelectionBar';

const ProjectOverviewView = ({ projects, onReload, isAdmin, storageByObservation = {} }) => {
  // Bulk select + delete of observations (shift-click range within a target,
  // ctrl/cmd-click toggle). Gated to admins, matching the per-row delete below.
  const selectionEnabled = !!isAdmin;
  const selection = useObservationSelection();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Optional: also delete the observations' uploaded image files (frees storage).
  // Defaults off — deleting the observation keeps its files (reachable in My Files) unless chosen.
  const [bulkDeleteFiles, setBulkDeleteFiles] = useState(false);
  // Per-observation S3 storage, keyed by observation id (from /user-files/by-observation).
  const getObservationBytes = (observationId) =>
    (observationId != null && storageByObservation[observationId.toString()]?.total_size) || 0;
  const getTargetBytes = (observations) =>
    (observations || []).reduce((sum, obs) => sum + getObservationBytes(obs._id), 0);

  console.log('ProjectOverviewView rendering with', projects.length, 'projects');
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [expandedTargets, setExpandedTargets] = useState(new Set());
  const [expandedObservations, setExpandedObservations] = useState(new Set());
  const [editDialog, setEditDialog] = useState({ open: false, observation: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, observation: null });
  const [editValues, setEditValues] = useState({ priority: 5, status: 'pending' });
  // Observations are loaded lazily per project when a row is expanded (#1504),
  // rather than fetched for every project up front. obsByProject caches the
  // fetched array per project id; obsLoadingIds tracks in-flight fetches.
  const [obsByProject, setObsByProject] = useState({});
  const [obsLoadingIds, setObsLoadingIds] = useState(() => new Set());

  const loadProjectObservations = useCallback(async (projectId) => {
    if (!projectId) return;
    setObsLoadingIds((prev) => new Set(prev).add(projectId));
    try {
      const res = await apiService.getObservations({ project_id: projectId, limit: 1000 });
      const observations = res.data?.observations || res.data || [];
      setObsByProject((prev) => ({ ...prev, [projectId]: observations }));
    } catch (err) {
      console.error(`Failed to load observations for project ${projectId}:`, err);
      setObsByProject((prev) => ({ ...prev, [projectId]: [] }));
    } finally {
      setObsLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  }, []);

  // Reload the parent project list (refreshes server-side summary counts) AND
  // re-fetch observations for any currently-expanded project so a mutation is
  // reflected immediately in the expanded detail.
  const reloadExpanded = useCallback(() => {
    onReload();
    expandedProjects.forEach((pid) => loadProjectObservations(pid));
  }, [onReload, expandedProjects, loadProjectObservations]);

  // Auto-expand project and target when returning from files view
  useEffect(() => {
    const state = location.state;
    if (state?.from === 'projects' && state?.projectId && projects.length > 0) {
      setExpandedProjects(new Set([state.projectId]));
      loadProjectObservations(state.projectId);
      if (state.targetName) {
        setExpandedTargets(new Set([`${state.projectId}-${state.targetName}`]));
      }
      // Restore scroll position after accordions have time to expand
      if (state.scrollY) {
        // Use multiple attempts to ensure scroll works after render
        const restoreScroll = () => window.scrollTo(0, state.scrollY);
        setTimeout(restoreScroll, 50);
        setTimeout(restoreScroll, 200);
        setTimeout(restoreScroll, 500);
      }
      // Clear the state so it doesn't re-expand on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state, projects.length, loadProjectObservations]);

  const toggleProject = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      // Lazy-load this project's observations on first expand; a re-expand reuses
      // the cached copy (#1504).
      if (obsByProject[projectId] === undefined && !obsLoadingIds.has(projectId)) {
        loadProjectObservations(projectId);
      }
    }
    setExpandedProjects(newExpanded);
  };

  const toggleTarget = (targetKey) => {
    const newExpanded = new Set(expandedTargets);
    if (newExpanded.has(targetKey)) {
      newExpanded.delete(targetKey);
    } else {
      newExpanded.add(targetKey);
    }
    setExpandedTargets(newExpanded);
  };

  const toggleObservation = (observationId) => {
    const newExpanded = new Set(expandedObservations);
    if (newExpanded.has(observationId)) {
      newExpanded.delete(observationId);
    } else {
      newExpanded.add(observationId);
    }
    setExpandedObservations(newExpanded);
  };

  // Group observations by target name
  const groupObservationsByTarget = (observations) => {
    if (!observations || observations.length === 0) return {};

    const grouped = {};
    observations.forEach(obs => {
      const targetName = obs.targets?.[0]?.name || obs.name || 'Unknown Target';
      if (!grouped[targetName]) {
        grouped[targetName] = {
          name: targetName,
          ra: obs.targets?.[0]?.ra,
          dec: obs.targets?.[0]?.dec,
          observations: []
        };
      }
      grouped[targetName].observations.push(obs);
    });
    return grouped;
  };

  // Calculate target stats
  const getTargetStats = (observations) => {
    const stats = { total: observations.length, pending: 0, running: 0, completed: 0, failed: 0 };
    observations.forEach(obs => {
      if (obs.status === 'pending') stats.pending++;
      else if (obs.status === 'in_progress' || obs.status === 'assigned') stats.running++;
      else if (obs.status === 'completed' || obs.status === 'completed_series' || obs.status === 'partially_completed') stats.completed++;
      else if (obs.status === 'failed') stats.failed++;
    });
    return stats;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'completed_series':
        return 'success';
      case 'partially_completed':
        return 'secondary';
      case 'in_progress':
      case 'assigned':
      case 'running':
      case 'acquisition_complete':
        return 'primary';
      case 'pending':
      case 'waiting_next':
        return 'default';
      case 'failed':
      case 'aborted':
      case 'expired':
        return 'error';
      case 'cancelled':
      case 'paused':
      case 'suspended':
        return 'warning';
      default:
        return 'default';
    }
  };

  const calculateTotalExposureTime = (observation) => {
    let totalSeconds = 0;

    // Check if this is a time-based observation
    const isTimeBased = observation.observation_type === 'time_based'
      || observation.observation_type === 'rise_to_set'
      || observation.observation_type === 'fixed_time'
      || observation.fill_time === true;

    // For completed observations, prefer actual recorded time
    if (observation.observation_summary?.total_exposure_time_seconds) {
      totalSeconds = observation.observation_summary.total_exposure_time_seconds;
    }
    else if (observation.exposure_plan?.total_exposure_time_seconds) {
      totalSeconds = observation.exposure_plan.total_exposure_time_seconds;
    }
    // For time-based, use time_limit_minutes if available
    else if (isTimeBased && observation.time_limit_minutes) {
      totalSeconds = observation.time_limit_minutes * 60;
    }
    else if (observation.estimated_duration_minutes) {
      totalSeconds = observation.estimated_duration_minutes * 60;
    }
    else if (observation.targets?.[0]?.exposure_configs) {
      observation.targets[0].exposure_configs.forEach(config => {
        totalSeconds += (config.exposure_time || 0) * (config.exposure_count || 1);
      });
    }

    // If we still have 0 or very small duration but have lots of files, show "-"
    if (totalSeconds < 60 && (observation.observation_summary?.files_captured > 10 || observation.files_captured > 10)) {
      return '-';
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else if (totalSeconds > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return '-';
    }
  };

  const getProgressInfo = (observation) => {
    // Check if this is a time-based observation
    const isTimeBased = observation.observation_type === 'time_based'
      || observation.observation_type === 'rise_to_set'
      || observation.observation_type === 'fixed_time'
      || observation.fill_time === true;

    // Get file counts from various sources
    // Note: files_captured can be a number or array depending on context
    const filesCaptured = observation.observation_summary?.files_captured
      || observation.captured_file_count
      || (typeof observation.files_captured === 'number' ? observation.files_captured : observation.files_captured?.length)
      || observation.actual_file_count
      || observation.frames_captured
      || observation.progress?.current_exposure
      || 0;

    // Calculate expected from exposure_configs if not set
    const calcExpectedFromConfigs = () => {
      const configs = observation.targets?.[0]?.exposure_configs || observation.exposure_configs || [];
      return configs.reduce((sum, cfg) => sum + (cfg.exposure_count || 1), 0);
    };

    const filesExpected = observation.observation_summary?.files_expected
      || observation.progress?.total_exposures
      || calcExpectedFromConfigs();

    // Get time acquired (in seconds)
    const timeAcquiredSeconds = observation.observation_summary?.total_exposure_time_seconds
      || observation.progress?.elapsed_time_seconds
      || 0;

    // Format time as Xh Ym
    const formatTime = (seconds) => {
      if (!seconds || seconds === 0) return null;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    };

    // For completed observations, always show image count
    if (observation.status === 'completed') {
      if (isTimeBased) {
        const timeText = formatTime(timeAcquiredSeconds);
        if (timeText && filesCaptured > 0) {
          return { text: `${filesCaptured} imgs (${timeText})`, hasProgress: false };
        }
        // Time-based but no time recorded - just show file count
        if (filesCaptured > 0) {
          return { text: `${filesCaptured} imgs`, hasProgress: false };
        }
      }
      if (filesCaptured > 0) {
        // Only show X/Y if the ratio makes sense (captured <= expected * 1.5)
        // Otherwise the expected count is likely wrong/placeholder
        if (filesExpected > 0 && filesCaptured <= filesExpected * 1.5) {
          return { text: `${filesCaptured}/${filesExpected} imgs`, hasProgress: false };
        }
        return { text: `${filesCaptured} imgs`, hasProgress: false };
      }
      return { text: 'Complete', hasProgress: false };
    }

    // For time-based observations in progress, show time acquired
    if (isTimeBased) {
      const timeText = formatTime(timeAcquiredSeconds);
      if (timeText) {
        return { text: timeText, hasProgress: false };
      }
      if (filesCaptured > 0) {
        return { text: `${filesCaptured} imgs`, hasProgress: false };
      }
      return { text: '-', hasProgress: false };
    }

    // For count-based observations, show file counts
    if (filesCaptured > 0 || filesExpected > 0) {
      if (filesExpected > 0) {
        return { text: `${filesCaptured}/${filesExpected}`, hasProgress: false };
      }
      return { text: `${filesCaptured} imgs`, hasProgress: false };
    }

    // For cancelled/aborted, show what was captured vs expected
    if (observation.status === 'cancelled' || observation.status === 'aborted') {
      if (filesExpected > 0) {
        return { text: `${filesCaptured}/${filesExpected}`, hasProgress: false };
      }
      if (filesCaptured > 0) {
        return { text: `${filesCaptured} imgs`, hasProgress: false };
      }
    }

    // Pending observations - show expected if known
    if (observation.status === 'pending') {
      if (filesExpected > 0 && !isTimeBased) {
        return { text: `0/${filesExpected}`, hasProgress: false };
      }
      return { text: '-', hasProgress: false };
    }

    // Fallback - show expected if available
    if (filesExpected > 0) {
      return { text: `${filesCaptured}/${filesExpected}`, hasProgress: false };
    }

    return { text: '-', hasProgress: false };
  };

  const hasExposurePlans = (observation) => {
    const exposures = observation.targets?.[0]?.exposure_plans || observation.targets?.[0]?.exposure_configs || observation.exposure_configs;
    return exposures?.length > 0;
  };

  const formatExposureTime = (seconds) => {
    if (seconds >= 60) {
      return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const handleEdit = (observation) => {
    setEditValues({
      priority: observation.priority || 5,
      status: observation.status
    });
    setEditDialog({ open: true, observation });
  };

  const handleDelete = (observation) => {
    setDeleteDialog({ open: true, observation });
  };

  const handleResubmit = async (observation) => {
    try {
      await apiService.updateObservationStatus(observation._id, 'pending', 'Resubmitted by user');
      reloadExpanded();
    } catch (err) {
      console.error('Failed to resubmit observation:', err);
    }
  };

  const handlePause = async (observation) => {
    try {
      await apiService.updateObservationStatus(observation._id, 'paused', 'Paused by user');
      reloadExpanded();
    } catch (err) {
      console.error('Failed to pause observation:', err);
    }
  };

  const handleResume = async (observation) => {
    try {
      await apiService.updateObservationStatus(observation._id, 'pending', 'Resumed by user');
      reloadExpanded();
    } catch (err) {
      console.error('Failed to resume observation:', err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const obs = editDialog.observation;
      await apiService.updateObservation(obs._id, {
        priority: editValues.priority,
        status: editValues.status
      });
      setEditDialog({ open: false, observation: null });
      reloadExpanded();
    } catch (err) {
      console.error('Failed to update observation:', err);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await apiService.deleteObservation(deleteDialog.observation._id);
      setDeleteDialog({ open: false, observation: null });
      reloadExpanded();
    } catch (err) {
      console.error('Failed to delete observation:', err);
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selection.selected);
    if (ids.length === 0) { setBulkDeleteOpen(false); return; }
    setBulkDeleting(true);
    try {
      // When bulkDeleteFiles is checked, also delete the uploaded image files
      // (frees storage); otherwise the observation is removed but its files are
      // kept and reachable in My Files.
      await apiService.bulkDeleteObservations(ids, bulkDeleteFiles);
      selection.clear();
      setBulkDeleteOpen(false);
      reloadExpanded();
    } catch (err) {
      console.error('Failed to bulk delete observations:', err);
    } finally {
      setBulkDeleting(false);
    }
  };

  // Show all projects, even those without observations, sorted alphabetically
  // by name (case-insensitive) so the Overview list has a stable A→Z order.
  const projectsToShow = projects.length > 0
    ? [...projects].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
    : [];

  if (projectsToShow.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" align="center">
          No projects found.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {selectionEnabled && (
        <SelectionBar
          count={selection.size}
          onClear={selection.clear}
          onDelete={() => { setBulkDeleteFiles(false); setBulkDeleteOpen(true); }}
        />
      )}
      <Stack spacing={2}>
        {projectsToShow.map((project) => {
          const stats = project.stats || {
            total: 0,
            running: 0,
            pending: 0,
            completed: 0,
            failed: 0
          };
          // Observations are loaded lazily on expand (#1504); undefined = not yet
          // fetched, [] = fetched-and-empty.
          const projectObservations = obsByProject[project._id];
          const observationsLoading = obsLoadingIds.has(project._id);

          return (
            <Accordion
              key={project._id}
              expanded={expandedProjects.has(project._id)}
              onChange={() => toggleProject(project._id)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <ProjectIcon sx={{ mr: 2, color: 'primary.main' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{project.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {project.code} • {project.owner_type === 'organization'
                        ? (project.owner_id?.name || project.owner_id?.short_name || '')
                        : `${project.owner_id?.first_name || ''} ${project.owner_id?.last_name || ''}`.trim()
                      }
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {project.storage_bytes > 0 && (
                      <Chip
                        label={formatSize(project.storage_bytes)}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {stats.running > 0 && (
                      <Chip
                        label={`${stats.running} Active`}
                        size="small"
                        color="primary"
                      />
                    )}
                    {stats.pending > 0 && (
                      <Chip
                        label={`${stats.pending} Pending`}
                        size="small"
                      />
                    )}
                    {stats.completed > 0 && (
                      <Chip
                        label={`${stats.completed} Complete`}
                        size="small"
                        color="success"
                      />
                    )}
                    {stats.failed > 0 && (
                      <Chip
                        label={`${stats.failed} Failed`}
                        size="small"
                        color="error"
                      />
                    )}
                  </Stack>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {observationsLoading ? (
                  <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (!projectObservations || projectObservations.length === 0) ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 3 }} align="center">
                    No observations in this project yet
                  </Typography>
                ) : (
                  <Box>
                    {Object.entries(groupObservationsByTarget(projectObservations)).map(([targetName, targetData]) => {
                      const targetKey = `${project._id}-${targetName}`;
                      const targetStats = getTargetStats(targetData.observations);
                      // Ordered observation ids for this target — range selection
                      // (shift-click) is confined to this target's table.
                      const orderedObsIds = targetData.observations.map((o) => o._id);
                      const obsIndex = new Map(orderedObsIds.map((id, i) => [id, i]));

                      return (
                        <Accordion
                          key={targetKey}
                          expanded={expandedTargets.has(targetKey)}
                          onChange={() => toggleTarget(targetKey)}
                          disableGutters
                          sx={{
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="subtitle1">{targetName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  RA: {targetData.ra?.toFixed(4) || '-'}, Dec: {targetData.dec?.toFixed(4) || '-'}
                                  {' • '}{targetStats.total} observation{targetStats.total !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1}>
                                {getTargetBytes(targetData.observations) > 0 && (
                                  <Chip label={formatSize(getTargetBytes(targetData.observations))} size="small" variant="outlined" />
                                )}
                                {targetStats.running > 0 && (
                                  <Chip label={`${targetStats.running} Active`} size="small" color="primary" />
                                )}
                                {targetStats.pending > 0 && (
                                  <Chip label={`${targetStats.pending} Pending`} size="small" />
                                )}
                                {targetStats.completed > 0 && (
                                  <Chip label={`${targetStats.completed} Complete`} size="small" color="success" />
                                )}
                                {targetStats.failed > 0 && (
                                  <Chip label={`${targetStats.failed} Failed`} size="small" color="error" />
                                )}
                              </Stack>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, pl: 4 }}>
                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell width="30"></TableCell>
                                    <TableCell>Observation</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Progress</TableCell>
                                    <TableCell>Files</TableCell>
                                    <TableCell>Priority</TableCell>
                                    {isAdmin && <TableCell>Actions</TableCell>}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {targetData.observations.map((obs) => {
                                    const progressInfo = getProgressInfo(obs);
                                    return (
                                      <Fragment key={obs._id}>
                                        <TableRow
                                          selected={selectionEnabled && selection.isSelected(obs._id)}
                                          title={selectionEnabled ? 'Shift/Ctrl-click to select for bulk delete' : undefined}
                                          sx={{
                                            '&:hover': { backgroundColor: 'action.hover' },
                                            cursor: (selectionEnabled || hasExposurePlans(obs)) ? 'pointer' : 'default',
                                            userSelect: 'none',
                                          }}
                                          // Shift/Ctrl/Cmd-click selects the row;
                                          // a plain click expands it as before.
                                          onClick={(e) => {
                                            if (selectionEnabled && (e.shiftKey || e.ctrlKey || e.metaKey)) {
                                              selection.handleClick(targetKey, obsIndex.get(obs._id), orderedObsIds, undefined, e);
                                            } else if (hasExposurePlans(obs)) {
                                              toggleObservation(obs._id);
                                            }
                                          }}
                                        >
                                          <TableCell>
                                            {hasExposurePlans(obs) && (
                                              <IconButton size="small" sx={{ padding: 0 }}>
                                                {expandedObservations.has(obs._id) ?
                                                  <KeyboardArrowDown fontSize="small" /> :
                                                  <KeyboardArrowRight fontSize="small" />
                                                }
                                              </IconButton>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {obs._id?.substring(0, 8) || 'Observation'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {obs.created_at ? new Date(obs.created_at).toLocaleDateString() : ''}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {calculateTotalExposureTime(obs)}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={String(obs.status || '').replace(/_/g, ' ')}
                                              size="small"
                                              color={getStatusColor(obs.status)}
                                              sx={{ textTransform: 'capitalize' }}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Typography variant="body2">
                                              {progressInfo.text}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                              // Check multiple sources for file count (same logic as getProgressInfo)
                                              const fileCount = obs.observation_summary?.files_captured
                                                || obs.captured_file_count
                                                || obs.files_captured
                                                || obs.actual_file_count
                                                || obs.frames_captured
                                                || obs.progress?.current_exposure
                                                || 0;
                                              const obsBytes = getObservationBytes(obs._id);
                                              return fileCount > 0 ? (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                  <Tooltip title={`View ${fileCount} file${fileCount !== 1 ? 's' : ''}`}>
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/observations/${obs._id}/files`, {
                                                          state: {
                                                            from: 'projects',
                                                            projectId: project._id,
                                                            targetName: targetName,
                                                            scrollY: window.scrollY
                                                          }
                                                        });
                                                      }}
                                                      color="primary"
                                                    >
                                                      <FolderIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  {obsBytes > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                      {formatSize(obsBytes)}
                                                    </Typography>
                                                  )}
                                                </Stack>
                                              ) : (
                                                <Typography variant="body2" color="text.secondary">-</Typography>
                                              );
                                            })()}
                                          </TableCell>
                                          <TableCell>
                                            {obs.priority || 5}
                                          </TableCell>
                                          {isAdmin && (
                                            <TableCell>
                                              <Stack direction="row" spacing={0.5}>
                                                <Tooltip title="Edit">
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleEdit(obs); }}
                                                  >
                                                    <EditIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                                {obs.status === 'pending' && (
                                                  <Tooltip title="Pause">
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => { e.stopPropagation(); handlePause(obs); }}
                                                    >
                                                      <PauseIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                {obs.status === 'paused' && (
                                                  <Tooltip title="Resume">
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => { e.stopPropagation(); handleResume(obs); }}
                                                    >
                                                      <PlayIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                {canResubmitObservation(obs.status) && (
                                                  <Tooltip title="Re-submit">
                                                    <IconButton
                                                      size="small"
                                                      onClick={(e) => { e.stopPropagation(); handleResubmit(obs); }}
                                                    >
                                                      <ReplayIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                                <Tooltip title="Delete">
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleDelete(obs); }}
                                                    color="error"
                                                  >
                                                    <DeleteIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              </Stack>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                        {hasExposurePlans(obs) && (
                                          <TableRow>
                                            <TableCell colSpan={isAdmin ? 8 : 7} sx={{ padding: 0, border: 'none' }}>
                                              <Collapse in={expandedObservations.has(obs._id)} timeout="auto" unmountOnExit>
                                                <Box sx={{ backgroundColor: 'action.hover', pl: 6, pr: 2, py: 1 }}>
                                                  <Table size="small">
                                                    <TableHead>
                                                      <TableRow>
                                                        <TableCell>Filter</TableCell>
                                                        <TableCell>Exposure</TableCell>
                                                        <TableCell>Count</TableCell>
                                                        <TableCell>Binning</TableCell>
                                                        <TableCell>Gain</TableCell>
                                                        <TableCell>Total Time</TableCell>
                                                      </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                      {(obs.targets?.[0]?.exposure_configs || obs.exposure_configs)?.map((plan, idx) => {
                                                        const totalTime = (plan.exposure_time || 0) * (plan.exposure_count || 1);
                                                        return (
                                                          <TableRow key={idx}>
                                                            <TableCell>{plan.filter || 'Clear'}</TableCell>
                                                            <TableCell>{formatExposureTime(plan.exposure_time || 0)}</TableCell>
                                                            <TableCell>{plan.exposure_count || 1}</TableCell>
                                                            <TableCell>{plan.binning ? `${plan.binning}x${plan.binning}` : '1x1'}</TableCell>
                                                            <TableCell>{plan.gain !== undefined ? plan.gain : '-'}</TableCell>
                                                            <TableCell>{formatExposureTime(totalTime)}</TableCell>
                                                          </TableRow>
                                                        );
                                                      })}
                                                    </TableBody>
                                                  </Table>
                                                </Box>
                                              </Collapse>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {/* Edit Dialog */}
      {isAdmin && (
        <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, observation: null })}>
          <DialogTitle>Edit Observation</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Priority"
                type="number"
                value={editValues.priority}
                onChange={(e) => setEditValues({ ...editValues, priority: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 10 }}
                fullWidth
              />
              <TextField
                label="Status"
                select
                value={editValues.status}
                onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
                fullWidth
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog({ open: false, observation: null })}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Dialog */}
      {isAdmin && (
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, observation: null })}>
          <DialogTitle>Delete Observation</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete observation "{deleteDialog.observation?.target?.name || deleteDialog.observation?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, observation: null })}>Cancel</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Bulk Delete Dialog */}
      {selectionEnabled && (
        <Dialog open={bulkDeleteOpen} onClose={() => !bulkDeleting && setBulkDeleteOpen(false)}>
          <DialogTitle>Delete Observations</DialogTitle>
          <DialogContent>
            <Typography>
              Delete <strong>{selection.size}</strong> selected observation{selection.size === 1 ? '' : 's'}?
              This cannot be undone.
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={bulkDeleteFiles}
                  onChange={(e) => setBulkDeleteFiles(e.target.checked)}
                  disabled={bulkDeleting}
                />
              }
              label="Also delete uploaded image files (frees storage)"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              {bulkDeleteFiles
                ? 'Image files will be permanently removed from storage.'
                : 'Image files are kept and remain reachable in My Files.'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</Button>
            <Button onClick={handleConfirmBulkDelete} color="error" variant="contained" disabled={bulkDeleting}>
              {bulkDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProjectOverviewView;